/**
 * Browser Gamepad API adapter — detect, poll, and dispatch gameplay/menu actions.
 */

import {
    GP_BTN,
    getGamepadClickSource,
    getGamepadDeadzone,
    getGamepadMenuBtn,
    getGamepadMenuReturnBtn,
    getGamepadPauseBtn,
    getGamepadRestartBtn,
    getGamepadReturnBtn,
    getGamepadSensitivity,
    type GamepadClickSource,
} from "./gamepad-settings";

export type GamepadFaceStyle = "xbox" | "playstation" | "generic";

export type GamepadCallbacks = {
    /** Analog cursor move (mouse-equivalent modes). */
    moveCursor: (dx: number, dy: number) => void;
    /** Fixed-speed directional move (WASD / D-Pad). */
    moveWasd: (nx: number, ny: number, dt: number) => void;
    /** Right stick aim (Hook). */
    aimHookStick: (nx: number, ny: number, deadZone: number) => void;
    pause: () => void;
    /** Toggle sidebar Menu (same as keybind Menu). */
    toggleMenu: () => void;
    returnDown: () => void;
    returnUp: () => void;
    quickRestartHoldComplete: () => void;
    primaryConfirm: () => void;
    superdotSlow: () => void;
    superdotBarrier: () => void;
    superdotShield: () => void;
    superdotPush: () => void;
    /** True while a run is active and input should affect the cursor. */
    isPlayingGameplay: () => boolean;
    /** True while menus / overlays should receive focus navigation. */
    isMenuNavActive: () => boolean;
    /** Mode id while playing (e.g. "wasd", "superdot", "hook", "tilt"). */
    getGameMode: () => string | null;
    onConnectionChange: (connected: boolean, pad: Gamepad | null) => void;
    showToast: (message: string) => void;
    /** Menu navigation tick (stick/dpad + confirm/back). */
    pollMenuNav: (nav: GamepadNavFrame) => void;
    /** Mark that a physical mouse recently moved (for priority). */
    noteMouseActivity?: () => void;
};

export type GamepadNavFrame = {
    moveX: number;
    moveY: number;
    confirm: boolean;
    back: boolean;
    dt: number;
};

const AXIS_LX = 0;
const AXIS_LY = 1;
const AXIS_RX = 2;
const AXIS_RY = 3;

const QUICK_RESTART_HOLD_MS = 850;
const MOUSE_PRIORITY_MS = 180;
const STICK_NAV_REPEAT_MS = 220;
const MENU_STICK_THRESHOLD = 0.55;

let mounted = false;
let cbs: GamepadCallbacks | null = null;
let activeIndex = -1;
let connected = false;
let lastId = "";
let faceStyle: GamepadFaceStyle = "generic";

const prevPressed: boolean[] = [];
let restartHoldStart = 0;
let returnHeld = false;
let lastMouseActivityMs = 0;

function now() {
    return performance.now();
}

export function noteGamepadMouseActivity() {
    lastMouseActivityMs = now();
    cbs?.noteMouseActivity?.();
}

/** True when a mouse recently moved — gameplay should prefer pointer over stick. */
export function isGamepadDeferringToMouse(): boolean {
    return now() - lastMouseActivityMs < MOUSE_PRIORITY_MS;
}

export function isGamepadConnected(): boolean {
    return connected && activeIndex >= 0;
}

export function getActiveGamepad(): Gamepad | null {
    if (!isGamepadConnected() || typeof navigator.getGamepads !== "function") return null;
    const pads = navigator.getGamepads();
    return pads[activeIndex] ?? null;
}

export function getGamepadFaceStyle(): GamepadFaceStyle {
    return faceStyle;
}

export function getGamepadDisplayName(): string {
    const pad = getActiveGamepad();
    if (!pad) return "None";
    return (pad.id || "Controller").replace(/\s+/g, " ").trim().slice(0, 64);
}

function detectFaceStyle(id: string): GamepadFaceStyle {
    const s = id.toLowerCase();
    if (/dualsense|dualshock|playstation|sony|ps[345]/.test(s)) return "playstation";
    if (/xbox|xinput|microsoft/.test(s)) return "xbox";
    return "generic";
}

/** Labels for Superdot: Slow/Barrier/Shield/Push → A/B/X/Y (or PS glyphs). */
export function getSuperdotGamepadHints(): { slow: string; barrier: string; shield: string; push: string } {
    if (faceStyle === "playstation") {
        return { slow: "×", barrier: "○", shield: "□", push: "△" };
    }
    return { slow: "A", barrier: "B", shield: "X", push: "Y" };
}

export function formatGamepadButtonLabel(index: number): string {
    const xbox: Record<number, string> = {
        [GP_BTN.A]: "A",
        [GP_BTN.B]: "B",
        [GP_BTN.X]: "X",
        [GP_BTN.Y]: "Y",
        [GP_BTN.LB]: "LB",
        [GP_BTN.RB]: "RB",
        [GP_BTN.LT]: "LT",
        [GP_BTN.RT]: "RT",
        [GP_BTN.SELECT]: "Back",
        [GP_BTN.START]: "Start",
        [GP_BTN.UP]: "D-Pad ↑",
        [GP_BTN.DOWN]: "D-Pad ↓",
        [GP_BTN.LEFT]: "D-Pad ←",
        [GP_BTN.RIGHT]: "D-Pad →",
    };
    const ps: Record<number, string> = {
        [GP_BTN.A]: "×",
        [GP_BTN.B]: "○",
        [GP_BTN.X]: "□",
        [GP_BTN.Y]: "△",
        [GP_BTN.LB]: "L1",
        [GP_BTN.RB]: "R1",
        [GP_BTN.LT]: "L2",
        [GP_BTN.RT]: "R2",
        [GP_BTN.SELECT]: "Share",
        [GP_BTN.START]: "Options",
        [GP_BTN.UP]: "D-Pad ↑",
        [GP_BTN.DOWN]: "D-Pad ↓",
        [GP_BTN.LEFT]: "D-Pad ←",
        [GP_BTN.RIGHT]: "D-Pad →",
    };
    const map = faceStyle === "playstation" ? ps : xbox;
    return map[index] ?? `Btn ${index}`;
}

function buttonValue(pad: Gamepad, index: number): number {
    const b = pad.buttons[index];
    if (!b) return 0;
    if (typeof b.value === "number" && Number.isFinite(b.value)) return b.value;
    return b.pressed ? 1 : 0;
}

function buttonPressed(pad: Gamepad, index: number): boolean {
    const b = pad.buttons[index];
    if (!b) return false;
    if (b.pressed) return true;
    return typeof b.value === "number" && b.value > 0.5;
}

function applyDeadzone(x: number, y: number, dead: number): { x: number; y: number; mag: number } {
    const mag = Math.hypot(x, y);
    if (mag < dead || mag < 1e-6) return { x: 0, y: 0, mag: 0 };
    const scale = (mag - dead) / (1 - dead);
    const ux = (x / mag) * Math.min(1, scale);
    const uy = (y / mag) * Math.min(1, scale);
    return { x: ux, y: uy, mag: Math.min(1, scale) };
}

function risingEdge(pad: Gamepad, index: number): boolean {
    const nowOn = buttonPressed(pad, index);
    const was = !!prevPressed[index];
    prevPressed[index] = nowOn;
    return nowOn && !was;
}

function syncPrevButtons(pad: Gamepad) {
    const n = Math.max(pad.buttons.length, 17);
    for (let i = 0; i < n; i++) {
        prevPressed[i] = buttonPressed(pad, i);
    }
}

function pickActivePad(): Gamepad | null {
    if (typeof navigator.getGamepads !== "function") return null;
    const pads = navigator.getGamepads();
    if (activeIndex >= 0) {
        const cur = pads[activeIndex];
        if (cur) return cur;
    }
    for (let i = 0; i < pads.length; i++) {
        const p = pads[i];
        if (p) {
            activeIndex = i;
            return p;
        }
    }
    activeIndex = -1;
    return null;
}

function setConnected(pad: Gamepad | null, announce: boolean) {
    const was = connected;
    connected = !!pad;
    if (pad) {
        activeIndex = pad.index;
        lastId = pad.id || "";
        faceStyle = detectFaceStyle(lastId);
        syncPrevButtons(pad);
    } else {
        activeIndex = -1;
        restartHoldStart = 0;
        if (returnHeld) {
            returnHeld = false;
            cbs?.returnUp();
        }
        for (let i = 0; i < prevPressed.length; i++) prevPressed[i] = false;
    }
    document.body.classList.toggle("gamepad-connected", connected);
    cbs?.onConnectionChange(connected, pad);
    if (announce && cbs) {
        if (connected && pad) {
            cbs.showToast(`Controller connected: ${getGamepadDisplayName()}`);
        } else if (was) {
            cbs.showToast("Controller disconnected");
        }
    }
}

function onConnected(e: GamepadEvent) {
    activeIndex = e.gamepad.index;
    setConnected(e.gamepad, true);
}

function onDisconnected(e: GamepadEvent) {
    if (e.gamepad.index === activeIndex) {
        // Prefer another still-connected pad if any.
        activeIndex = -1;
        const next = pickActivePad();
        setConnected(next, true);
    }
}

function isClickPressed(pad: Gamepad, source: GamepadClickSource): boolean {
    const a = buttonPressed(pad, GP_BTN.A);
    const rt = buttonPressed(pad, GP_BTN.RT) || buttonValue(pad, GP_BTN.RT) > 0.55;
    if (source === "a") return a;
    if (source === "rt") return rt;
    return a || rt;
}

function wasClickRising(pad: Gamepad, source: GamepadClickSource): boolean {
    const aRise = risingEdge(pad, GP_BTN.A);
    // RT may be analog — treat crossing 0.55 as edge via prevPressed[7]
    const rtNow = buttonPressed(pad, GP_BTN.RT) || buttonValue(pad, GP_BTN.RT) > 0.55;
    const rtWas = !!prevPressed[GP_BTN.RT];
    const rtRise = rtNow && !rtWas;
    prevPressed[GP_BTN.RT] = rtNow;
    // A edge already updated prevPressed in risingEdge; if we also check RT, A was updated.
    if (source === "a") return aRise;
    if (source === "rt") {
        // Don't consume A edge for click when RT-only — still update A prev via separate track
        return rtRise;
    }
    return aRise || rtRise;
}

/**
 * Max cursor speed (px/s) from sensitivity slider (1–20).
 * Matches floating touch joystick feel (~640 at sens 4 → scaled).
 */
function cursorSpeedPxPerSec(sensitivity: number): number {
    return 640 * (sensitivity / 4);
}

export function mountGamepadInput(callbacks: GamepadCallbacks) {
    if (mounted) {
        cbs = callbacks;
        return;
    }
    mounted = true;
    cbs = callbacks;

    window.addEventListener("gamepadconnected", onConnected);
    window.addEventListener("gamepaddisconnected", onDisconnected);

    // Capture mouse so stick doesn't fight pointer lock / mouse aim.
    window.addEventListener(
        "pointermove",
        (e) => {
            if (e.pointerType === "mouse" && (e.movementX !== 0 || e.movementY !== 0)) {
                noteGamepadMouseActivity();
            }
        },
        { passive: true },
    );

    // Initial scan (some browsers only expose pads after a button press).
    const existing = pickActivePad();
    if (existing) setConnected(existing, false);
}

export function refreshGamepadConnectionScan() {
    const pad = pickActivePad();
    if (pad && !connected) setConnected(pad, true);
    else if (!pad && connected) setConnected(null, true);
    else if (pad) {
        faceStyle = detectFaceStyle(pad.id || "");
        document.body.classList.toggle("gamepad-connected", true);
    }
}

/**
 * Poll once per frame. Call from the main rAF loop (menus + gameplay).
 */
export function pollGamepadInput(dt: number) {
    if (!cbs) return;
    const pad = pickActivePad();
    if (!pad) {
        if (connected) setConnected(null, true);
        return;
    }
    if (!connected || pad.index !== activeIndex) {
        setConnected(pad, !connected);
    }

    const mode = cbs.getGameMode();
    const playing = cbs.isPlayingGameplay();
    const menu = cbs.isMenuNavActive();
    const dead = getGamepadDeadzone();
    const sens = getGamepadSensitivity();
    const clickSrc = getGamepadClickSource();

    const lx = pad.axes[AXIS_LX] ?? 0;
    const ly = pad.axes[AXIS_LY] ?? 0;
    const rx = pad.axes[AXIS_RX] ?? 0;
    const ry = pad.axes[AXIS_RY] ?? 0;
    const left = applyDeadzone(lx, ly, dead);
    const right = applyDeadzone(rx, ry, dead);

    const dpadX = (buttonPressed(pad, GP_BTN.RIGHT) ? 1 : 0) - (buttonPressed(pad, GP_BTN.LEFT) ? 1 : 0);
    const dpadY = (buttonPressed(pad, GP_BTN.DOWN) ? 1 : 0) - (buttonPressed(pad, GP_BTN.UP) ? 1 : 0);

    if (menu && !playing) {
        // Start still pauses/resumes from overlays.
        if (risingEdge(pad, getGamepadPauseBtn())) cbs.pause();

        const menuBtn = getGamepadMenuBtn();
        if (risingEdge(pad, menuBtn)) cbs.toggleMenu();

        const confirm = risingEdge(pad, GP_BTN.A);
        const backBtn = getGamepadMenuReturnBtn();
        const back = risingEdge(pad, backBtn);
        const handled = new Set([GP_BTN.A, backBtn, getGamepadPauseBtn(), menuBtn]);
        for (let i = 0; i < pad.buttons.length; i++) {
            if (handled.has(i)) continue;
            prevPressed[i] = buttonPressed(pad, i);
        }
        for (const i of handled) {
            prevPressed[i] = buttonPressed(pad, i);
        }

        const moveX =
            Math.abs(left.x) > MENU_STICK_THRESHOLD || dpadX !== 0
                ? dpadX !== 0
                    ? dpadX
                    : Math.sign(left.x)
                : 0;
        const moveY =
            Math.abs(left.y) > MENU_STICK_THRESHOLD || dpadY !== 0
                ? dpadY !== 0
                    ? dpadY
                    : Math.sign(left.y)
                : 0;
        cbs.pollMenuNav({ moveX, moveY, confirm, back, dt });
        return;
    }

    if (!playing || mode === "tilt") {
        // Still allow pause/return while paused overlays, etc.
        pollGlobalButtons(pad, playing);
        syncRemainingPrev(pad);
        return;
    }

    const mousePriority = now() - lastMouseActivityMs < MOUSE_PRIORITY_MS;

    // Movement
    if (mode === "wasd") {
        let nx = dpadX;
        let ny = dpadY;
        if (nx === 0 && ny === 0 && left.mag > 0) {
            // Optional: allow stick as digital WASD when past half tilt
            nx = Math.abs(left.x) > 0.45 ? Math.sign(left.x) : 0;
            ny = Math.abs(left.y) > 0.45 ? Math.sign(left.y) : 0;
        }
        if (nx !== 0 || ny !== 0) {
            const len = Math.hypot(nx, ny) || 1;
            cbs.moveWasd(nx / len, ny / len, dt);
        }
    } else if (!mousePriority && left.mag > 0) {
        const sp = cursorSpeedPxPerSec(sens) * left.mag;
        cbs.moveCursor(left.x * sp * dt, left.y * sp * dt);
    }

    if (mode === "hook" && right.mag > 0) {
        cbs.aimHookStick(right.x, right.y, dead);
    }

    // Superdot face buttons (A/B/X/Y → Q/W/E/R)
    if (mode === "superdot") {
        if (risingEdge(pad, GP_BTN.A)) cbs.superdotSlow();
        if (risingEdge(pad, GP_BTN.B)) cbs.superdotBarrier();
        if (risingEdge(pad, GP_BTN.X)) cbs.superdotShield();
        if (risingEdge(pad, GP_BTN.Y)) cbs.superdotPush();
    } else {
        // Primary confirm (AIM / Hook / Golf / Counter / default click)
        // Avoid double-firing A when it's also used for superdot.
        if (wasClickRising(pad, clickSrc)) {
            // wasClickRising updates A and RT prev; still need other buttons
            cbs.primaryConfirm();
        } else if (clickSrc === "rt") {
            // Keep A prev in sync even when RT-only click
            prevPressed[GP_BTN.A] = buttonPressed(pad, GP_BTN.A);
        }
    }

    pollGlobalButtons(pad, true);
    syncRemainingPrev(pad);
}

function pollGlobalButtons(pad: Gamepad, _playing: boolean) {
    if (!cbs) return;
    const pauseBtn = getGamepadPauseBtn();
    const returnBtn = getGamepadReturnBtn();
    const restartBtn = getGamepadRestartBtn();
    const menuBtn = getGamepadMenuBtn();

    if (risingEdge(pad, pauseBtn)) {
        cbs.pause();
    }

    if (risingEdge(pad, menuBtn)) {
        cbs.toggleMenu();
    }

    const retOn = buttonPressed(pad, returnBtn);
    if (retOn && !returnHeld) {
        returnHeld = true;
        cbs.returnDown();
    } else if (!retOn && returnHeld) {
        returnHeld = false;
        cbs.returnUp();
    }

    const restartOn = buttonPressed(pad, restartBtn);
    if (restartOn) {
        if (!restartHoldStart) restartHoldStart = now();
        else if (now() - restartHoldStart >= QUICK_RESTART_HOLD_MS) {
            restartHoldStart = now() + 1e9; // prevent re-fire until release
            cbs.quickRestartHoldComplete();
        }
    } else {
        restartHoldStart = 0;
    }
}

function syncRemainingPrev(pad: Gamepad) {
    for (let i = 0; i < pad.buttons.length; i++) {
        if (prevPressed[i] === undefined) prevPressed[i] = false;
        // Don't overwrite edges already handled this frame for A/RT/pause/return — 
        // safest to always sync from current state after handling.
        prevPressed[i] = buttonPressed(pad, i);
        if (i === GP_BTN.RT) {
            prevPressed[i] = buttonPressed(pad, i) || buttonValue(pad, i) > 0.55;
        }
    }
}

/** Expose stick nav repeat helper for menu module. */
export const GAMEPAD_MENU_STICK_REPEAT_MS = STICK_NAV_REPEAT_MS;
